import React from 'react'

import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx'
import { inject, observer, Provider } from 'mobx-react'

import { SitePermitStatus } from '~/client/graph'
import { ISiteLocation } from '~/client/graph/types.generated'
import BaseMapView from '~/client/src/shared/components/BaseMapView/BaseMapView'
import { composeLabelGround } from '~/client/src/shared/components/MapBoxViewer/labelGround'
import { IGroundContext } from '~/client/src/shared/components/MapBoxViewer/labelGroundTone'
import MapBoxViewerStore from '~/client/src/shared/components/MapBoxViewer/MapBoxViewer.store'
import { SESSION_MARKUP_PREFIX } from '~/client/src/shared/components/MapBoxViewer/markupTextLayerIds'
import { MarkupEditorTextChannel } from '~/client/src/shared/components/MapMarkups/markupEditorTextChannel'
import { markupLabelsFor } from '~/client/src/shared/components/MapMarkups/markupLabels'
import MarkupLayer, {
  MARKUP_LABEL_POPUP_OFFSET_PX,
} from '~/client/src/shared/components/MapMarkups/MarkupLayer'
import GeneralMapPopup, {
  MapPopupBody,
} from '~/client/src/shared/components/MapPopups/GeneralMapPopup'
import PermitListItem from '~/client/src/shared/components/PermitListItem'
import BaseMapViewSetUpStore from '~/client/src/shared/components/SitemapHelpers/BaseMapViewSetUp.store'
import DomMovablePin from '~/client/src/shared/components/SitemapHelpers/components/DomPins/DomMovablePin'
import DomPin from '~/client/src/shared/components/SitemapHelpers/components/DomPins/DomPin'
import GlobeViewFiles from '~/client/src/shared/components/SitemapHelpers/components/GlobeViewFiles/GlobeViewFiles'
import GlobeViewItems from '~/client/src/shared/components/SitemapHelpers/components/GlobeViewItems'
import GLWorkflowPinMarker from '~/client/src/shared/components/SitemapHelpers/components/GLWorkflowPinMarker'
import MapViewItemBase from '~/client/src/shared/components/SitemapHelpers/models/MapViewItemBase'
import { WorkflowGlyph } from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowIconGroups'
import { getWorkflowStatusPinFill } from '~/client/src/shared/constants/workflowStatusColors'
import GlobeView from '~/client/src/shared/models/GlobeView'
import Markup from '~/client/src/shared/models/Markup'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import BasemapsStore from '~/client/src/shared/stores/domain/Basemaps.store'
import GlobeViewsStore from '~/client/src/shared/stores/domain/GlobeViews.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MarkupsStore from '~/client/src/shared/stores/domain/Markups.store'
import SitemapItemsStore from '~/client/src/shared/stores/domain/SitemapItems.store'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import { hasPreciseCoordinates } from '~/client/src/shared/utils/locationSnapshot'

import { resolveViewportForPreciseLocation } from '../preciseLocationHelpers'

// Not prefixed 'workflow-pins': mapboxLayerOrder matches pins by prefix and would pull this
// into the markup ordering it enforces on the workflows map.
const RESTING_PIN_SOURCE_ID = 'precise-location-pin-source'
const RESTING_PIN_LAYER_ID = 'precise-location-pin'

// What the map needs to know about the workflow behind the pin. Deliberately the state and
// not a colour, so one source resolves the raster's image id and the DOM fill alike.
export interface IPreciseLocationPinState {
  status?: SitePermitStatus
  isLate?: boolean
  isAssigned?: boolean
}

interface IProps {
  value: ISiteLocation
  mapBoxViewerStore: MapBoxViewerStore
  globe?: GlobeView

  isExpanded?: boolean
  isEditMode?: boolean
  pinCoordinates?: { latitude: number; longitude: number } | null
  permitType?: WorkflowType
  selectedStartDate?: Date
  selectedEndDate?: Date
  sitemapsStore?: SitemapsStore
  globeViewsStore?: GlobeViewsStore
  basemapsStore?: BasemapsStore
  sitemapItemsStore?: SitemapItemsStore
  locationAttributesStore?: LocationAttributesStore
  state?: InitialState
  isMobileEnv?: boolean
  sitePermitId?: string
  // True while creating a workflow: sitePermitId is a client draft id with no
  // server row, so its markups must not be fetched.
  isDeferred?: boolean
  isMarkupMode?: boolean
  canMovePin?: boolean
  // The workflow's STATE, not a resolved colour: the resting pin is a GL raster keyed off
  // this, and the pins drawn in the DOM want a fill from it. Absent means a workflow being
  // written rather than a saved one, which resolves to the draft grey and no star.
  pinState?: IPreciseLocationPinState
  collapsed?: boolean
  onEditPreciseLocation?: () => void
  markupsStore?: MarkupsStore
  copySelectedId?: string | null
  // This Workflow's text: the markup editor's while it is attached.
  editorText?: MarkupEditorTextChannel
  fullscreenTargetRef?: React.RefObject<HTMLElement>
}

const SITEMAPITEM_ZOOM_LEVEL = 18
const NON_GEOLOCATED_ZOOM_LEVEL = 18
const REF_LABEL_HOVER_SOURCE = 'markup-ref-labels-capture'

@inject(
  'sitemapsStore',
  'globeViewsStore',
  'basemapsStore',
  'sitemapItemsStore',
  'locationAttributesStore',
  'state',
  'isMobileEnv',
  'markupsStore',
)
@observer
export default class FormPreciseLocationMap extends React.Component<IProps> {
  private readonly store: BaseMapViewSetUpStore = null
  @observable private localContext = null
  @observable private readonly mapBoxViewerStore: MapBoxViewerStore = null
  @observable private localProps: IProps = null
  @observable private permitType: WorkflowType = null
  @observable private globe: GlobeView = null
  @observable private selectedStartDate: Date = null
  @observable private selectedEndDate: Date = null
  @observable private isExpanded: boolean = false
  @observable private isEditMode: boolean = false
  @observable private isMarkupMode: boolean = false
  @observable private canMovePin: boolean = false
  // Mirrored for the same reason copySelectedId is: renderCenterPin runs inside
  // MapBoxViewer's render, so reading this.props there trips mobx-react's guard.
  @observable private pinState: IPreciseLocationPinState = null
  @observable private collapsed: boolean = false
  @observable private sitePermitId: string = null
  // Copied from props: reading this.props in this @observer's render trips mobx-react's "props in a derivation" guard.
  @observable private copySelectedId: string | null = null
  // Stable for the form's life; copied for the same props-in-derivation reason.
  private readonly editorText: MarkupEditorTextChannel = null
  // The adjacent markup whose card is open, at the label point that was clicked.
  @observable.ref private infoMarkup: {
    id: string
    point: { longitude: number; latitude: number }
  } | null = null
  @observable private pinCoordinates: {
    latitude: number
    longitude: number
  } | null = null
  private mapRefDisposer: () => void = null
  private captureContainer: HTMLElement | null = null
  private captureCanvas: HTMLCanvasElement | null = null

  public UNSAFE_componentWillMount(): void {
    this.localContext = this.context
  }

  public constructor(props: IProps) {
    super(props)
    this.localProps = props

    this.store = new BaseMapViewSetUpStore(
      props.sitemapsStore,
      props.globeViewsStore,
      props.basemapsStore,
      props.sitemapItemsStore,
      props.locationAttributesStore,
    )

    this.mapBoxViewerStore = props.mapBoxViewerStore
    this.permitType = props.permitType
    this.globe = props.globe
    this.selectedStartDate = props.selectedStartDate
    this.selectedEndDate = props.selectedEndDate
    this.isExpanded = props.isExpanded
    this.isEditMode = props.isEditMode
    this.isMarkupMode = props.isMarkupMode
    this.canMovePin = props.canMovePin
    this.pinState = props.pinState
    this.collapsed = props.collapsed
    this.sitePermitId = props.sitePermitId
    this.copySelectedId = props.copySelectedId
    this.editorText = props.editorText
    this.pinCoordinates = props.pinCoordinates

    if (!props.value?.preciseLocation && !props.value?.id) {
      props.mapBoxViewerStore.setViewportFromAddress()
    }

    props.mapBoxViewerStore.setDefaultMapMode()
    this.setViewport()
    makeObservable(this)
  }

  public componentDidMount(): void {
    this.ensureMarkupsLoaded()
    this.mapRefDisposer = reaction(
      () => this.mapBoxViewerStore.mapRef,
      () => {
        this.syncRefLabelCapture()
      },
      { fireImmediately: true },
    )
  }

  public componentWillUnmount(): void {
    this.localProps.markupsStore?.setEditingSitePermitId(null)
    this.mapRefDisposer?.()
    this.detachRefLabelCapture()
  }

  // Pin the own workflow so the Logistics eviction pass can't drop it while the modal
  // is open, then lazy-load the own + visible reference markups it renders.
  private ensureMarkupsLoaded(): void {
    const store = this.localProps.markupsStore
    if (!store) return
    store.setEditingSitePermitId(this.sitePermitId)
    const ids = [
      // Skip the own id while it's still the client draft id (creation) — its
      // markups live only in the local buffer, so loading it is an empty fetch.
      this.localProps.isDeferred ? null : this.sitePermitId,
      ...(store.visibleWorkflows
        ? Array.from(store.visibleWorkflows.keys())
        : []),
    ].filter(Boolean)
    store.ensureLoadedForSitePermits(ids)
  }

  public componentDidUpdate(prevProps: Readonly<IProps>): void {
    const nextProps = this.props

    runInAction(() => {
      // Update isExpanded FIRST so setViewport uses the correct mode
      // for zoom adjustment (collapsed vs expanded).
      let needsViewportUpdate = false
      let viewportValue: ISiteLocation | undefined = nextProps.value
      if (prevProps.isExpanded !== nextProps.isExpanded) {
        this.isExpanded = nextProps.isExpanded
        needsViewportUpdate = true
      }
      if (prevProps.value !== nextProps.value) {
        needsViewportUpdate = true
      }
      if (prevProps.permitType !== nextProps.permitType) {
        this.permitType = nextProps.permitType
      }
      if (prevProps.globe !== nextProps.globe) {
        this.globe = nextProps.globe
        // Globe switch mid-move: the pin sits at pinCoordinates, not in value yet —
        // center on it so the camera doesn't fly to the stale saved location.
        const pl = nextProps.value?.preciseLocation
        const pin = nextProps.pinCoordinates
        // Merge only a real pin: an image-only record merged with pin coords
        // would pass the resolver and freeze the PREVIOUS globe's camera
        // instead of adopting the new globe's default viewport.
        viewportValue =
          hasPreciseCoordinates(pl) && pin
            ? { ...nextProps.value, preciseLocation: { ...pl, ...pin } }
            : nextProps.value
        needsViewportUpdate = true
      }
      if (needsViewportUpdate) {
        this.setViewport(viewportValue)
      }
      if (prevProps.selectedStartDate !== nextProps.selectedStartDate) {
        this.selectedStartDate = nextProps.selectedStartDate
      }
      if (prevProps.selectedEndDate !== nextProps.selectedEndDate) {
        this.selectedEndDate = nextProps.selectedEndDate
      }
      if (prevProps.isEditMode !== nextProps.isEditMode) {
        this.isEditMode = nextProps.isEditMode
      }
      if (prevProps.isMarkupMode !== nextProps.isMarkupMode) {
        this.isMarkupMode = nextProps.isMarkupMode
      }
      if (prevProps.canMovePin !== nextProps.canMovePin) {
        this.canMovePin = nextProps.canMovePin
      }
      if (prevProps.pinState !== nextProps.pinState) {
        this.pinState = nextProps.pinState
      }
      if (prevProps.collapsed !== nextProps.collapsed) {
        this.collapsed = nextProps.collapsed
      }
      if (prevProps.sitePermitId !== nextProps.sitePermitId) {
        this.sitePermitId = nextProps.sitePermitId
        this.ensureMarkupsLoaded()
      }
      if (prevProps.copySelectedId !== nextProps.copySelectedId) {
        this.copySelectedId = nextProps.copySelectedId
      }
      if (
        prevProps.pinCoordinates?.latitude !==
          nextProps.pinCoordinates?.latitude ||
        prevProps.pinCoordinates?.longitude !==
          nextProps.pinCoordinates?.longitude
      ) {
        this.pinCoordinates = nextProps.pinCoordinates
        // Inherited coords can hydrate after mount → re-seat. Skip while editing:
        // the user owns the camera, and "Done" reassigns pinCoordinates (don't reset it).
        if (
          nextProps.pinCoordinates &&
          !hasPreciseCoordinates(nextProps.value?.preciseLocation) &&
          !nextProps.isMarkupMode
        ) {
          this.setViewport(nextProps.value)
        }
      }
    })
  }

  private setViewport(value?: ISiteLocation) {
    const v = value ?? this.localProps.value
    const nextViewport = resolveViewportForPreciseLocation({
      preciseLocation: v?.preciseLocation,
      selectedGlobe: this.mapBoxViewerStore.selectedGlobe,
      currentViewport: this.mapBoxViewerStore.viewport,
    })
    if (!nextViewport) {
      this.setViewportWithDefaultZoom(v)
      return
    }

    this.mapBoxViewerStore.jumpToViewport(nextViewport)
  }

  private setViewportWithDefaultZoom(value?: ISiteLocation) {
    const v = value ?? this.localProps.value
    const coords =
      this.pinCoordinates ??
      (v?.id
        ? this.localProps.globeViewsStore?.effectiveLocationCoordinates(v.id)
        : null)
    if (coords) {
      const { viewport, selectedGlobe } = this.mapBoxViewerStore
      this.mapBoxViewerStore.jumpToViewport({
        latitude: coords.latitude,
        longitude: coords.longitude,
        zoom: SITEMAPITEM_ZOOM_LEVEL,
        ...(selectedGlobe && {
          bearing: selectedGlobe.bearing ?? viewport.bearing,
          pitch: selectedGlobe.pitch ?? viewport.pitch,
        }),
      })
      return
    }
    this.mapBoxViewerStore.setViewportFromAddress(
      undefined,
      undefined,
      NON_GEOLOCATED_ZOOM_LEVEL,
    )
  }

  // One resolution for both renderers. A pin with no saved workflow behind it resolves to the
  // closed grey, which is what a draft wears.
  @computed
  private get pinFill(): string {
    return getWorkflowStatusPinFill({
      status: this.pinState?.status,
      isLate: this.pinState?.isLate,
    })
  }

  // Exactly one pin is ever drawn, and this is the only place that decides which. Three
  // render sites read it, so they cannot overlap the way separate booleans did — the raster
  // and the movable pin were both passing an isEditMode-only gate and drawing at once.
  //   collapsed          -> 'raster':   the map's own image, on its coordinate. The pin a
  //                                     workflow report prints.
  //   expanded, at rest  -> 'movable':  DOM, because the Move affordance anchors to it.
  //   expanded, dragging -> 'dragging': DOM, pinned to the viewport while the map pans under.
  @computed
  private get pinMode(): 'raster' | 'movable' | 'dragging' {
    if (this.isEditMode) return 'dragging'
    return this.isExpanded ? 'movable' : 'raster'
  }

  private get isPreviewMode(): boolean {
    return this.pinMode === 'movable'
  }

  public render() {
    const { mapBoxViewerStore, globe } = this
    const { state } = this.localProps
    const { sitemapUrl } = this.store
    const isInteractive = this.isExpanded

    return (
      <div className="relative general-map-container full-width full-height">
        <BaseMapView
          globe={globe}
          isDeliveryView={true}
          sitemapUrl={sitemapUrl}
          mapBoxViewerStore={mapBoxViewerStore}
          projectAddress={state.projectAddress}
          isMobileEnv={this.props.isMobileEnv}
          isDraggable={isInteractive}
          shouldHideControls={!this.isExpanded}
          disableMapInteractions={!isInteractive}
          fullscreenTargetRef={this.props.fullscreenTargetRef}
          // Only while the pin is being dragged: a DOM pin fixed to the viewport centre is
          // what "pan the map under the pin" needs. At rest the pin is a GL symbol on its
          // coordinate — the same raster the workflows map draws.
          renderCenterMarker={
            this.pinMode === 'dragging' ? this.renderCenterPin : undefined
          }
        >
          {() => {
            return (
              <Provider {...this.localContext}>
                {this.renderGlobeViewChildren()}
                {this.isPreviewMode && this.pinCoordinates && (
                  <DomMovablePin
                    latitude={this.pinCoordinates.latitude}
                    longitude={this.pinCoordinates.longitude}
                    iconName={this.permitType?.iconName}
                    fill={this.pinFill}
                    isAssigned={!!this.pinState?.isAssigned}
                    canMove={this.canMovePin}
                    collapsed={this.collapsed}
                    onMove={this.localProps.onEditPreciseLocation}
                  />
                )}
                {/* Before MapBoxViewer's center marker, so the dragged pin sits
                    above the dim instead of being washed out by it. */}
                {this.isEditMode && (
                  <div className="tw:pointer-events-none tw:absolute tw:inset-0 tw:z-[5] tw:bg-white/25" />
                )}
              </Provider>
            )
          }}
        </BaseMapView>
      </div>
    )
  }

  private renderGlobeViewChildren = () => {
    const { displayedGlobeViewItems } = this.mapBoxViewerStore
    return (
      <>
        <GlobeViewFiles
          mapBoxViewerStore={this.mapBoxViewerStore}
          sitemapWithBasemaps={
            this.mapBoxViewerStore.sitemapWithBasemapsOnGlobe
          }
          mapFilesOnGlobe={this.mapBoxViewerStore.mapFilesOnGlobe}
        />
        <GlobeViewItems
          items={displayedGlobeViewItems || []}
          ground={this.labelGround}
          mapBoxViewerStore={this.mapBoxViewerStore}
          isDisabled={this.isExpanded}
          selectedStartDate={this.selectedStartDate}
          selectedEndDate={this.selectedEndDate}
        />
        <MarkupLayer
          isZoomScalingEnabled={
            this.mapBoxViewerStore.globeViewStyle.isZoomScalingEnabled
          }
          idPrefix={SESSION_MARKUP_PREFIX}
          markups={this.ownGlobeMarkups}
          ground={this.labelGround}
          editorText={this.editorText}
        />
        <MarkupLayer
          isZoomScalingEnabled={
            this.mapBoxViewerStore.globeViewStyle.isZoomScalingEnabled
          }
          idPrefix="markup-ref"
          muted={true}
          ground={this.labelGround}
          markups={this.referenceMarkups}
          labels={this.refMarkupLabels}
          activeWorkflowIds={this.activeInfoWorkflowIds}
          allowOverlap={
            this.mapBoxViewerStore.globeViewStyle.isLabelOverlapEnabled
          }
          selectedId={this.isMarkupMode ? this.copySelectedId : null}
          visible={
            this.isExpanded &&
            this.mapBoxViewerStore.markupPrefs.showAdjacentMarkups
          }
        />
        {this.renderRestingPin()}
        {this.renderWorkflowInfoCard()}
      </>
    )
  }

  // Mounted last so it draws over the location markers and their labels. Never decluttered:
  // a thumbnail has one pin and it is the subject, so it cannot be allowed to lose a collision
  // with a label — and no occlusion opt-in either, since a pin the report needs must not be
  // hidden by 3D content.
  private renderRestingPin(): JSX.Element {
    if (this.pinMode !== 'raster' || !this.pinCoordinates) {
      return null
    }
    return (
      <GLWorkflowPinMarker
        sourceId={RESTING_PIN_SOURCE_ID}
        layerId={RESTING_PIN_LAYER_ID}
        latitude={this.pinCoordinates.latitude}
        longitude={this.pinCoordinates.longitude}
        iconName={this.permitType?.iconName}
        isAssigned={this.pinState?.isAssigned}
        status={this.pinState?.status}
        isLate={this.pinState?.isLate}
      />
    )
  }

  // terra-draw owns selection + cursor via its own canvas listeners — only
  // capture-phase listeners run first, letting a label open just the info card.
  private syncRefLabelCapture(): void {
    const container = this.mapBoxViewerStore.mapRef
      ?.getMap?.()
      ?.getCanvasContainer?.()
    if (container === this.captureContainer) {
      return
    }
    this.detachRefLabelCapture()
    if (!container) {
      return
    }
    this.captureContainer = container
    this.captureCanvas = this.mapBoxViewerStore.mapRef.getMap().getCanvas()
    // terra-draw selects on the down+up pair — block both; click opens the card.
    container.addEventListener('pointerdown', this.onRefLabelPointerDown, true)
    container.addEventListener('pointerup', this.onRefLabelPointerUp, true)
    container.addEventListener('click', this.onRefLabelClick, true)
    container.addEventListener('mousemove', this.onRefLabelMove, true)
  }

  private detachRefLabelCapture(): void {
    const container = this.captureContainer
    if (!container) {
      return
    }
    container.removeEventListener(
      'pointerdown',
      this.onRefLabelPointerDown,
      true,
    )
    container.removeEventListener('pointerup', this.onRefLabelPointerUp, true)
    container.removeEventListener('click', this.onRefLabelClick, true)
    container.removeEventListener('mousemove', this.onRefLabelMove, true)
    this.mapBoxViewerStore.setOverlayPointerHovered(
      false,
      REF_LABEL_HOVER_SOURCE,
    )
    this.captureContainer = null
    this.captureCanvas = null
  }

  private refLabelAt(
    clientX: number,
    clientY: number,
  ): mapboxgl.MapboxGeoJSONFeature | undefined {
    const map = this.mapBoxViewerStore.mapRef?.getMap?.()
    const canvas = this.captureCanvas
    if (!map || !canvas || !map.getLayer('markup-ref-labels')) {
      return undefined
    }
    const rect = canvas.getBoundingClientRect()
    const hit = map.queryRenderedFeatures(
      [clientX - rect.left, clientY - rect.top],
      {
        layers: ['markup-ref-labels'],
      },
    )[0]
    return hit?.properties?.markupId ? hit : undefined
  }

  private onRefLabelPointerDown = (e: PointerEvent): void => {
    if (this.refLabelAt(e.clientX, e.clientY)) {
      e.stopImmediatePropagation()
      e.preventDefault()
    }
  }

  private onRefLabelPointerUp = (e: PointerEvent): void => {
    if (this.refLabelAt(e.clientX, e.clientY)) {
      e.stopImmediatePropagation()
    }
  }

  private onRefLabelClick = (e: MouseEvent): void => {
    const label = this.refLabelAt(e.clientX, e.clientY)
    if (label) {
      e.stopImmediatePropagation()
      const [longitude, latitude] = (label.geometry as GeoJSON.Point)
        .coordinates
      this.showWorkflowInfo(label.properties.markupId as string, {
        longitude,
        latitude,
      })
    }
  }

  // Sync (no rAF): the stop must fire in-event, so the map's own move handlers never see a
  // label. That includes the label layer's hover probe, so the pointer is reported from here.
  private onRefLabelMove = (e: MouseEvent): void => {
    const isOverLabel = !!this.refLabelAt(e.clientX, e.clientY)
    this.mapBoxViewerStore.setOverlayPointerHovered(
      isOverLabel,
      REF_LABEL_HOVER_SOURCE,
    )
    if (isOverLabel) {
      e.stopImmediatePropagation()
    }
  }

  @action.bound
  private showWorkflowInfo(
    id: string,
    point: { longitude: number; latitude: number },
  ): void {
    this.infoMarkup = { id, point }
  }

  @action.bound
  private clearWorkflowInfo(): void {
    this.infoMarkup = null
  }

  private renderWorkflowInfoCard(): JSX.Element {
    // Opened from an adjacent markup's chip — only valid while that layer shows.
    if (
      !this.isExpanded ||
      !this.mapBoxViewerStore.markupPrefs.showAdjacentMarkups
    ) {
      return null
    }
    const info = this.infoMarkup
    const markup = info ? this.localProps.markupsStore?.byId.get(info.id) : null
    const permit = markup
      ? this.localProps.markupsStore?.visibleWorkflows?.get(markup.sitePermitId)
      : null
    if (!markup?.coordinates?.length || !permit) return null
    const location = this.localProps.locationAttributesStore?.getById(
      permit.locations[0]?.id,
    )
    return (
      <GeneralMapPopup
        item={
          {
            dataObject: location,
            displayName: location?.name,
          } as unknown as MapViewItemBase
        }
        coordOverride={info.point}
        offset={MARKUP_LABEL_POPUP_OFFSET_PX}
        onClose={this.clearWorkflowInfo}
      >
        <MapPopupBody>
          <PermitListItem permit={permit} isSelected={true} />
        </MapPopupBody>
      </GeneralMapPopup>
    )
  }

  private get selectedGlobeViewId(): string {
    return this.mapBoxViewerStore.globeViewControlStore.selectedGlobeViewId
  }

  @computed
  private get labelGround(): IGroundContext {
    return composeLabelGround(
      this.mapBoxViewerStore.isSatelliteActive,
      this.mapBoxViewerStore.displayedGlobeViewItems ?? [],
      [...this.referenceMarkups, ...this.ownGlobeMarkups],
    )
  }

  // @computed so the arrays/maps keep a stable reference across camera-frame re-renders,
  // letting the memoized MarkupLayer skip re-render during pan/tilt/rotate.
  @computed
  private get ownGlobeMarkups(): Markup[] {
    const store = this.localProps.markupsStore
    if (!store || !this.sitePermitId) return []
    return store.forGlobe(
      store.getBySitePermitId(this.sitePermitId),
      this.selectedGlobeViewId,
    )
  }

  @computed
  private get referenceMarkups(): Markup[] {
    const store = this.localProps.markupsStore
    if (!store || !this.sitePermitId) return []
    const visible = store.visibleWorkflows
    return store
      .forGlobe(store.persistedList, this.selectedGlobeViewId)
      .filter(
        m =>
          m.sitePermitId !== this.sitePermitId &&
          (visible ? visible.has(m.sitePermitId) : true),
      )
  }

  @computed
  private get refMarkupLabels(): Map<string, string> {
    return markupLabelsFor(this.localProps.markupsStore?.visibleWorkflows)
  }

  @computed
  private get activeInfoWorkflowIds(): Set<string> {
    const active = this.infoMarkup
      ? this.localProps.markupsStore?.byId.get(this.infoMarkup.id)
      : undefined
    return active ? new Set([active.sitePermitId]) : new Set()
  }

  private renderCenterPin = (): JSX.Element => {
    return (
      <DomPin
        overlay
        // Only ever rendered while dragging, so it is always the grown size and the move colour.
        size="lg"
        glyph={({ size, color }) => (
          <WorkflowGlyph
            iconName={this.permitType?.iconName}
            size={size}
            color={color}
          />
        )}
        fill="var(--primary)"
      />
    )
  }
}
